import { section, number, choice, table, example, quantitative } from './shared.ts';
export const batch2Reason = [
  quantitative(
    'reason-rooms',
    'reason',
    'Meeting rooms',
    [
      section('concept', 'Shared resources and overlapping intervals', [
        'A room can serve one meeting at a time. The challenge is to reuse rooms without allowing two simultaneous meetings to occupy the same room. Represent each meeting as an interval with a starting and ending time. Here an ending time frees a room immediately: a meeting ending at 10:00 does not conflict with one beginning at 10:00. Requiring cleanup time would change these intervals.',
        'A minimum requires two arguments. A lower bound explains why fewer rooms cannot work. A construction assigns every meeting to an actual room. A bound alone may underestimate the answer, while an assignment alone may waste rooms. When they meet at the same number, optimality is proved.',
        'At any instant, each meeting in progress needs a different room. The largest simultaneous overlap therefore supplies a lower bound. To build an assignment, process starts chronologically, reuse any free room, and open a new room only when every existing room is occupied. Whenever the procedure opens a room, the meetings then in progress certify that fewer rooms would be impossible.',
      ]),
      section('example', 'Track capacity over time', [
        example(
          'Three meetings',
          'Meetings run 09:00–10:00, 09:30–10:30 and 10:00–11:00. Two overlap between 09:30 and 10:00, ruling out one room. Put the first and third in room A, and the second in room B.',
          table(
            'A feasible allocation',
            ['Room', 'First meeting', 'Next meeting'],
            [
              ['A', '09:00–10:00', '10:00–11:00'],
              ['B', '09:30–10:30', '—'],
            ],
          ),
          'The middle meeting spans the other two but does not force a third room. Sharing an endpoint is different from sharing a positive interval of time.',
        ),
        'To count overlaps systematically, make an event list. Each start adds one to the number in progress, and each end subtracts one. At a shared time process ends before starts. The maximum running count measures required capacity; the total number of starts does not.',
      ]),
      section(
        'application',
        'Apply the overlap bound',
        [
          'A, B, C and D all run between 09:45 and 10:00 in the daily schedule, so at least four rooms are necessary. The following four-room assignment proves sufficiency.',
          table(
            'Daily assignment',
            ['Room', 'Meetings in order'],
            [
              ['1', 'A, E, G'],
              ['2', 'B, F'],
              ['3', 'C'],
              ['4', 'D'],
            ],
          ),
          'A and C finish before E starts at 10:00; B finishes as F begins at 10:30; G begins as E ends. Every meeting appears once. Counting every meeting that touches 10:00 would overestimate simultaneous occupancy. Giving each meeting a permanent room would ignore reuse. Both mistakes confuse the number of tasks with the capacity needed to serve overlapping tasks.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b2-rooms',
      'Meetings A–G have fixed times: A 09:00–10:00; B 09:15–10:30; C 09:30–10:00; D 09:45–11:00; E 10:00–11:15; F 10:30–11:30; G 11:15–12:00. A room holds one meeting at a time and can be reused immediately when one ends. What is the minimum number of rooms needed?',
      4,
      'rooms',
      'A, B, C and D overlap. Four rooms suffice with A–E–G, B–F, C and D.',
      ['concept', 'example', 'application'],
    ),
  ),
  quantitative(
    'reason-runnerup',
    'reason',
    'The runner-up',
    [
      section('concept', 'Comparisons as evidence', [
        'Comparing two objects gives one fact about their relative order. Assume all strengths differ and comparisons always report the stronger object. To certify the strongest, every other object must have lost at least once: an object that never lost could still be strongest without contradicting any observation. Each comparison creates only one loser, so finding the strongest among n objects requires at least n − 1 comparisons.',
        'The runner-up need not be the champion’s final opponent. A very strong object might face the champion early. However, anyone who loses to an object other than the champion cannot be second strongest: both its victor and the champion outrank it. Once the champion is known, only its direct opponents can still be second.',
      ]),
      section('example', 'Follow the champion’s path', [
        example(
          'Eight objects',
          'A balanced knockout tournament with eight objects uses four first-round comparisons, two semifinals and one final: seven comparisons. The champion directly defeats three opponents, one per round.',
          table(
            'Candidate reduction',
            ['Observed result', 'Still possibly second?', 'Reason'],
            [
              ['Lost directly to champion', 'Yes', 'No other object necessarily outranks it'],
              ['Lost to someone else', 'No', 'Its victor and the champion both outrank it'],
            ],
          ),
          'Finding the strongest of the three direct losers takes two more comparisons. Nine comparisons in total suffice, without sorting all eight objects.',
        ),
        'The tournament results act as a certificate. Retain the observations relevant to the next question instead of attempting to reconstruct the entire order. The direct losers came from different branches, so their earlier wins do not order them relative to each other. Any one of them could be runner-up under some ordering consistent with the tournament.',
      ]),
      section(
        'application',
        'Four remaining candidates',
        [
          'The specified sixteen-object tournament has four rounds and fifteen comparisons. Its champion has four direct opponents, each still a possible runner-up. Compare two pairs of those candidates, then compare the winners. This needs three additional comparisons, or eighteen including the original tournament.',
          'Three are necessary as well as sufficient. Starting with four possible runner-ups requires eliminating three. Each new comparison eliminates at most one candidate. Comparing a candidate against a weaker object elsewhere may eliminate none, so it cannot improve the lower bound. Two additional comparisons leave at least two candidates undefeated within the candidate set.',
          'The word additional matters: fifteen comparisons have already been spent and are not part of the requested answer. The argument also uses the balanced bracket specified in the question. An arbitrary first-stage comparison schedule need not leave exactly four direct losers. Distinct strengths avoid ties, which would otherwise require a definition of second place and a tie-handling rule.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b2-runnerup',
      'Sixteen objects have distinct fixed strengths. Comparisons always identify the stronger object. A balanced single-elimination tournament of four rounds has already found the strongest, and every result was recorded. What is the minimum number of ADDITIONAL comparisons needed to guarantee identification of the second-strongest object?',
      3,
      'additional comparisons',
      'Only the champion’s four direct opponents can be second. Finding the strongest of those four requires and suffices with three comparisons.',
      ['concept', 'example', 'application'],
    ),
  ),
  quantitative(
    'reason-numbercards',
    'reason',
    'Number cards',
    [
      section('concept', 'Guarantees and adversarial choices', [
        'A guarantee must survive the least cooperative allowed selection. Instead of asking how quickly a useful pair might appear, ask how many choices can avoid every useful pair. One more than that maximum forces the event. This differs from an average waiting time: the prompt need not supply a probability distribution at all.',
        'The pigeonhole principle says that placing more objects than boxes forces some box to contain multiple objects. The important step is constructing useful boxes. For a target sum, pair each allowed number with its complement. If every number belongs to exactly one pair, choosing both members of any box produces the target.',
      ]),
      section('example', 'Partition by complementary values', [
        example(
          'Numbers from 1 to 10',
          'Seek two distinct numbers summing to 11. The boxes are {1,10}, {2,9}, {3,8}, {4,7} and {5,6}. Six chosen numbers must fill one of these five boxes.',
          table(
            'Sufficiency and necessity',
            ['Selection', 'What it establishes'],
            [
              ['Any six distinct numbers', 'A complementary pair is forced'],
              ['The five numbers 1,2,3,4,5', 'Five can still avoid the target'],
            ],
          ),
          'The second row matters: it proves that the sufficient count cannot be reduced. Without a failing smaller selection, the argument would not yet prove a minimum.',
        ),
        'Check the partition before applying the principle. A number can sometimes be its own complement: 5 for a target of 10. One card numbered 5 cannot pair with itself if cards must be distinct. An odd target avoids that special case in the example, but a changed range or target may leave singletons and need a different argument. Boxes must cover the actual allowed population, not an imagined symmetric extension.',
      ]),
      section(
        'application',
        'Ten boxes for twenty cards',
        [
          'The daily complementary pairs are {1,20}, {2,19}, continuing through {10,11}. They form ten disjoint boxes containing all twenty cards. Eleven draws force one box to contain two selected cards, so a sum of 21 is guaranteed.',
          'Ten draws may fail: choose 1 through 10. The two largest distinct choices, 9 and 10, sum to only 19. This construction rules out every claimed guarantee of ten or fewer, since subsets of that selection fail too. The lower and upper arguments therefore meet at eleven.',
          'Random draws would often find a pair sooner, but that observation cannot improve a worst-case guarantee. Repeated drawing with replacement would be a different problem: repeatedly drawing 1 could avoid success indefinitely. Both the distinct-card population and the no-replacement rule are doing real work in the proof.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b2-numbercards',
      'A box contains one card bearing each integer from 1 through 20. Cards are drawn without replacement, with no assumption that the draw is random. How many cards must you draw to GUARANTEE that two drawn cards have numbers summing to 21?',
      11,
      'cards',
      'Ten complementary pairs partition the cards. Eleven draws force a pair; drawing 1 through 10 shows ten need not suffice.',
      ['concept', 'example', 'application'],
    ),
  ),
  quantitative(
    'reason-lamps',
    'reason',
    'A row of lamps',
    [
      section('concept', 'Look for a preserved property', [
        'A state describes the system at one moment; a move changes that state according to a rule. Searching every possible move sequence can be wasteful. Sometimes a property survives every legal move. Such a property is called an invariant. If the starting state and target differ in that property, the target cannot be reached.',
        'Parity means whether an integer is even or odd. Adding or subtracting two preserves parity. Toggling a lamp reverses its state: on becomes off and off becomes on. Toggling two distinct lamps changes the number on by two, minus two, or zero, depending on their previous states.',
      ]),
      section('method', 'Cover every kind of move', [
        table(
          'Two-lamp toggles',
          ['Before', 'After', 'Change in number on'],
          [
            ['Both off', 'Both on', '+2'],
            ['Both on', 'Both off', '−2'],
            ['One on, one off', 'One off, one on', '0'],
          ],
        ),
        'The table is exhaustive, so parity preservation applies to every move, not merely a few examples. Repeating preserving moves still preserves the property. Starting with zero lamps on means every reachable state has an even number on.',
        example(
          'Six lamps',
          'Two lamps can be turned on by toggling that pair. Four can be turned on with two disjoint pairs, and six with three pairs. Exactly three cannot be reached from all off, because it is odd.',
        ),
        'An invariant usually provides only a necessary condition: passing it does not automatically show that a state is reachable. In this particular puzzle we can prove sufficiency as well. Take any desired even-sized subset, divide it into disjoint pairs, and toggle each pair once. That reaches exactly the chosen subset and leaves every other lamp off. Thus the even-count states are precisely the reachable states.',
      ]),
      section(
        'application',
        'Seven of twelve',
        [
          'The target count seven is odd, whereas the starting count zero is even. No sequence of the permitted moves reaches it. Overlapping pairs and repeated toggles are already covered by the exhaustive move table; neither changes this conclusion.',
          'Working backward from all twelve on does not evade the invariant. Twelve is even, and removing or exchanging lamps through two-lamp toggles still preserves parity. The impossibility is not a limit on how often a lamp may change: it may change arbitrarily many times. It is a limit on what one move can change.',
          'If the rules allowed a single one-lamp toggle, it would be possible to move into the odd-count class and then reach any desired odd subset with pair toggles. This changed-rule check explains exactly where the obstruction comes from. It belongs to the move rule, not to the physical lamps or the number twelve.',
        ],
        'Daily application',
      ),
    ],
    choice(
      'b2-lamps',
      'Twelve lamps begin off. Each move toggles exactly two distinct lamps; any pair may be chosen on any move. Can a sequence leave exactly seven lamps on?',
      [
        'Yes, by choosing pairs that overlap.',
        'No, because the parity of the number on is preserved.',
        'Yes, but only if some lamps are toggled repeatedly.',
        'No, because each lamp may change state only once.',
      ],
      1,
      'The count changes by −2, 0 or +2 and stays even. Repeated toggles are allowed but do not change this invariant.',
      ['concept', 'method', 'application'],
    ),
  ),
  quantitative(
    'reason-workshop',
    'reason',
    'Workshop jobs',
    [
      section('concept', 'Capacity and indivisible jobs', [
        'The finish time for parallel machines is when the last machine becomes free. Each machine performs its assigned jobs sequentially, while different machines work simultaneously. If a job cannot be interrupted or split, an assignment must place the whole job on one machine. Dividing total work equally on paper does not necessarily give a feasible schedule.',
        'Two immediate lower bounds are the longest job and total work divided by the number of machines. The longest job has to finish somewhere. For the second bound, m machines running for T minutes supply at most mT machine-minutes of work. Indivisible jobs can force the real answer above both bounds.',
      ]),
      section('example', 'Why an average can underestimate', [
        example(
          'Three equal jobs',
          'Three jobs each take four minutes, and two machines are available. Twelve machine-minutes divided by two suggests a six-minute lower bound. But no machine can fit two four-minute jobs within six minutes, so only two jobs could finish in that window.',
          table(
            'A feasible schedule',
            ['Machine', 'Jobs', 'Finish'],
            [
              ['A', '4 then 4', '8 minutes'],
              ['B', '4', '4 minutes'],
            ],
          ),
          'Eight minutes is both possible and necessary. The example separates total capacity from feasible packing of whole jobs.',
        ),
        'For a small instance, test a proposed finish time by asking which jobs can share a machine without exceeding it. Start with large jobs because they have fewer compatible partners. This is a useful search order, not a proof that every greedy allocation is optimal. The proof needs a bound ruling out all smaller finish times and an explicit assignment attaining it.',
      ]),
      section(
        'application',
        'Pack forty minutes of work',
        [
          'The daily jobs total forty minutes. Dividing by three gives 13⅓ minutes. An optimal assignment never needs idle time before its assigned jobs: removing that idle time cannot delay any job. Since job durations are integers, the resulting machine loads are integers. Finishing below fourteen would therefore require all three loads to be at most thirteen, allowing only thirty-nine minutes of work.',
          table(
            'A schedule meeting the bound',
            ['Machine', 'Jobs', 'Load'],
            [
              ['A', '9 and 5', '14'],
              ['B', '8 and 6', '14'],
              ['C', '7 and 5', '12'],
            ],
          ),
          'Fourteen minutes works, so it is the minimum. All six jobs appear once, including the two separate five-minute jobs. Nothing has been split or moved between machines. Unequal machine speeds, setup times or dependencies between jobs would introduce new constraints; simple sums would then no longer be a complete description. Here the stated assumptions make the capacity bound and packing construction sufficient.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b2-workshop',
      'Three identical machines are available at time zero. Six independent jobs take 9, 8, 7, 6, 5 and 5 minutes. Each machine runs one job at a time; jobs cannot be interrupted, split or moved after starting. There is no setup time. What is the earliest time by which all jobs can finish?',
      14,
      'minutes',
      'Forty minutes cannot fit into three integer loads of at most thirteen. Loads 9+5, 8+6 and 7+5 attain fourteen.',
      ['concept', 'example', 'application'],
    ),
  ),
];
